import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import addStylist from 'actions/stylist/addStylist.action';
import validateEmail from 'bl/validation/validateEmail.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import CardWaitlist from 'components/waitlist/CardWaitlist.component';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import PERMISSION_LABELS from 'constants/permissionLabels.const';
import USER_ROLE from 'constants/role';
import EMPLOYMENT_NAME from 'constants/staff/employmentName.const';
import EMPLOYMENT_TYPE from 'constants/staff/employmentType.const';
import useAsyncFnFix from 'hooks/useAsyncFnFix.hook';
import { useNavFor } from 'hooks/useNav.hook';
import useTip from 'hooks/useTip.hook';
import useWindowScroll from 'hooks/useWindowScroll.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';
import equalStrings from 'utils/strings/equalStrings.util';
import C from 'utils/values/constant.util';

import { getRoles } from 'onboarding/roles/actions';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';

import CN from './CreateWaitlist.module.scss';

const L = logger('CreateWaitlist');

const PERMISSIONS = C(['no_access', 'receptionist', 'manager']);
const SELF_TYPE = C(['employed', 'selfEmployed']);

const CreateWaitlist = ({ roles, stylists, getRoles, addStylist }) => {
  useWindowScroll(0, 0);

  const nav = useNavFor('CreateWaitlist()');
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [salonData, setSalonData] = useState({
    name: '',
    email: '',
  });
  const [emailConfirm, setEmailConfirm] = useState('');
  const [role, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(USER_ROLE.noAccess);
  const [employmentType, setEmploymentType] = useState(EMPLOYMENT_TYPE.employed);

  const { val: renderRequiredEmailWarning, yay: showWarning, nay: hideWarning } = useTip(false);

  const { val: isNameValid, set: setIsNameValid } = useTip(false);
  const { val: isEmailValid, set: setIsEmailValid } = useTip(true);
  const { val: isEmailTouched, yay: markIsEmailTouched } = useTip(false);

  const { val: isEmailConfirmTouched, set: setEmailConfirmTouched } = useTip(false);
  const { val: emailsMatch, set: setEmailsMatch } = useTip(true);

  const { val: isRoleOpen, nay: closeRole, tip: toggleRole } = useTip(false);
  const { val: isPermissionOpen, nay: closePermissions, tip: togglePermissions } = useTip(false);
  const { val: isEmploymentOpen, nay: closeEmployment, tip: toggleEmployment } = useTip(false);

  const { val: successfulModal, tip: toggleSuccessfulModal } = useTip(false);
  const { val: errorModal, yay: showErrorModal, nay: hideErrorModal } = useTip(false);

  const handleGoBack = useCallback(() => {
    nav.push({
      pathname: linkTo({ pattern: LINK.settingStylist, params: { salonId }, fault: '#' }),
    });
  }, [nav, salonId]);

  const handleInputChange = (field, value) => {
    setSalonData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'email') {
      const isValid = validateEmail({ value });
      setIsEmailValid(isValid);
      setEmailsMatch(value === emailConfirm);
    }
    if (field === 'name') void setIsNameValid(value.trim() !== '');
  };

  const handleEmailConfirmChange = (value) => {
    setEmailConfirm(value);
    setEmailsMatch(salonData.email === value);
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    closeRole();
  };

  const handleSelectPermission = (value) => {
    const willRequireEmail = value !== USER_ROLE.noAccess;
    const emailMissing = !validateEmail({ value: salonData.email });

    if (willRequireEmail && emailMissing) {
      showWarning();
      closePermissions();
      delayed(DELAY.autohide, () => void hideWarning());
      return;
    }

    setSelectedPermission(value);
    closePermissions();
  };

  const handleSelectEmployment = (value) => {
    setEmploymentType(value);
    closeEmployment();
  };

  const [, handleSave] = useAsyncFnFix(async () => {
    if (!salonData) return;

    const stylistData = {
      title: salonData.name,
      user_name: salonData.name,
      email: salonData.email,
      serves_as_waitlist: true,
      role: {
        id: role?.id || roles?.roles?.[0]?.id,
        name: role?.name || roles?.roles?.[0]?.name,
      },
      has_column: true,
      permission_level: selectedPermission,
      is_self_employed: employmentType === EMPLOYMENT_TYPE.selfEmployed,
    };

    const result = await addStylist({ stylist: stylistData, salonId });

    if (result.is.ok) {
      toggleSuccessfulModal();
    } else {
      L.warn('handleSave()', 'result:', result);
    }

    const existingEmails = stylists?.map((s) => s?.email) || [];
    const currentEmail = salonData?.email;

    if (currentEmail) {
      const isDuplicate = existingEmails.some((email) => equalStrings(email, currentEmail));

      if (isDuplicate) {
        showErrorModal();
        return;
      }
    }
  }, [
    salonData,
    role?.id,
    role?.name,
    roles?.roles,
    selectedPermission,
    employmentType,
    addStylist,
    salonId,
    stylists,
    toggleSuccessfulModal,
    showErrorModal,
  ]);

  const isEmailRequired = selectedPermission !== USER_ROLE.noAccess;
  const isPermissionValid = !(
    salonData.email &&
    validateEmail({ value: salonData.email }) &&
    selectedPermission === USER_ROLE.noAccess
  );
  const isFormValid =
    isNameValid &&
    (!isEmailRequired || (isEmailValid && emailsMatch && !isEmptyString(salonData.email))) &&
    isPermissionValid;

  useEffect(() => {
    if (roles?.roles && isEmptyArray(roles.roles) && !role) {
      getRoles(salonId);
    }
  }, [getRoles, role, roles, salonId]);

  return (
    <div
      data-bem="CreateWaitlist"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.titleContainer}>
        <div className={CN.title}>{t('stylistScreen.createWaitlist.title')}</div>
        <div className={CN.buttonContainer}>
          <Button variant="icon" width="small" onClick={handleGoBack} tag="CreateWaitlist__close">
            <Icon variant="closeDark" width="lg" />
          </Button>
        </div>
      </div>

      <div className={CN.container}>
        <div data-bem="CreateWaitlist__form">
          <div className={CN.inputContainer}>
            <span className={CN.inputLabel}>{t('stylistScreen.inputFields.nameLabel')}</span>
            <input
              type="text"
              value={salonData?.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={markIsEmailTouched}
              className={cn({
                [CN.input]: true,
                [CN.invalid]: !isNameValid,
              })}
              placeholder={t('stylistScreen.inputFields.namePlaceholder1')}
              tabIndex={1}
            />

            {isEmailTouched && !isNameValid && (
              <span className={CN.error}>{t('common.errors.validation.nameRequired')}</span>
            )}
          </div>

          <div className={CN.inputContainer}>
            <span className={CN.inputLabel}>{t('stylistScreen.inputFields.emailLabel')}</span>
            <input
              type="email"
              value={salonData?.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={cn({
                [CN.input]: true,
                [CN.invalid]: !isEmailValid,
              })}
              placeholder={t('stylistScreen.inputFields.emailPlaceholder')}
              tabIndex={2}
            />
            {isEmailRequired && !isEmailValid && (
              <span className={CN.error}>{t('common.errors.validation.notValidEmail')}</span>
            )}
          </div>

          <div className={CN.inputContainer}>
            <span className={CN.inputLabel}>{t('stylistScreen.inputFields.reEnterEmailLabel')}</span>
            <input
              type="email"
              value={emailConfirm}
              onChange={(e) => handleEmailConfirmChange(e.target.value)}
              onBlur={setEmailConfirmTouched}
              className={cn({
                [CN.input]: true,
                [CN.invalid]: isEmailConfirmTouched && !emailsMatch,
              })}
              placeholder={t('stylistScreen.inputFields.reEnterEmailLabel')}
              tabIndex={3}
            />
            {isEmailConfirmTouched && !emailsMatch && (
              <span className={CN.error}>{t('common.errors.validation.notMatchedEmail')}</span>
            )}
          </div>

          <div className={CN.inputContainer}>
            <div className={CN.inputLabel}>{t('stylistScreen.inputFields.jobLabel')}</div>
            <div className={CN.dropdownContainer}>
              <div
                onClick={toggleRole}
                className={cn({
                  [CN.dropdownButton]: true,
                  [CN.active]: isRoleOpen,
                })}
              >
                <div>{role?.name || roles?.roles?.[0]?.name || t('stylistScreen.createWaitlist.select')}</div>
                <Icon variant="arrowTriangle" width="xs" />
              </div>

              {isRoleOpen && (
                <DropdownFoldout width="full" position="bottom" onClickOutside={closeRole}>
                  <DropdownFoldout.Body>
                    <div className={CN.dropdownContent}>
                      {roles?.roles?.map((role) => (
                        <div key={role.id} className={CN.item} onClick={() => handleSelectRole(role)}>
                          {role.name}
                        </div>
                      ))}
                    </div>
                  </DropdownFoldout.Body>
                </DropdownFoldout>
              )}
            </div>
          </div>

          <div className={CN.inputContainer}>
            <div className={CN.inputLabel}>{t('stylistScreen.inputFields.employmentLabel')}</div>
            <div className={CN.dropdownContainer}>
              <div
                onClick={toggleEmployment}
                className={cn({
                  [CN.dropdownButton]: true,
                  [CN.active]: isEmploymentOpen,
                })}
              >
                <div>{t(EMPLOYMENT_NAME[employmentType])}</div>
                <Icon variant="arrowTriangle" width="xs" />
              </div>

              {isEmploymentOpen && (
                <DropdownFoldout width="full" position="bottom" onClickOutside={closeEmployment}>
                  <DropdownFoldout.Body>
                    <div className={CN.dropdownContent}>
                      {SELF_TYPE.map((key) => (
                        <div key={key} className={CN.item} onClick={() => handleSelectEmployment(key)}>
                          {t(EMPLOYMENT_NAME[key])}
                        </div>
                      ))}
                    </div>
                  </DropdownFoldout.Body>
                </DropdownFoldout>
              )}
            </div>
          </div>

          <div className={CN.inputContainer}>
            <div className={CN.inputLabel}>{t('stylistScreen.inputFields.selectPermission')}</div>
            <div className={CN.dropdownContainer}>
              <div
                onClick={togglePermissions}
                className={cn({
                  [CN.dropdownButton]: true,
                  [CN.active]: isPermissionOpen,
                })}
              >
                <div>{t(PERMISSION_LABELS[selectedPermission])}</div>
                <Icon variant="arrowTriangle" width="xs" />
              </div>

              {isPermissionOpen && (
                <DropdownFoldout width="full" position="bottom" onClickOutside={closePermissions}>
                  <DropdownFoldout.Body>
                    <div className={CN.dropdownContent}>
                      {PERMISSIONS.map((key) => (
                        <div key={key} onClick={() => handleSelectPermission(key)} className={CN.item}>
                          {t(PERMISSION_LABELS[key])}
                        </div>
                      ))}
                    </div>
                  </DropdownFoldout.Body>
                </DropdownFoldout>
              )}
            </div>
            {renderRequiredEmailWarning && (
              <div className={CN.error}>{t('stylistScreen.createWaitlist.permissionInputError')}</div>
            )}
          </div>
        </div>

        <CardWaitlist tag="CreateWaitlist__cardWaitlist" />
      </div>

      <SectionSeparator />

      {!isPermissionValid && (
        <div
          className={cn({
            [CN.error]: true,
            [CN.inFooter]: true,
          })}
        >
          {t('stylistScreen.createWaitlist.validPermissionMsg')}
        </div>
      )}
      <div className={CN.saveButton}>
        <Button variant="primary" onClick={handleSave} xSpace disabled={!isFormValid} tag="CardWaitlist__save-button">
          {t('common.buttons.saveChanges')}
        </Button>
      </div>

      <SalonModal tag="CreateWaitlist__modal_successfully__added" isOpen={successfulModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={handleGoBack}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon selected width="l1" variant="confirm" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('stylistScreen.createWaitlist.successModalMsg')}</div>
            <div className={CN.modalText}></div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="CreateWaitlist__modal_duplicate__emails" isOpen={errorModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={hideErrorModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('stylistScreen.createWaitlist.duplicateEmailsModal.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.createWaitlist.duplicateEmailsModal.description')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

const CreateWaitlistHoc = connect(
  (state) => ({
    roles: state.onboarding.roles,
    stylists: state.onboarding.stylists.stylists,
    pricingPlan: selectCurrentSalonPricingPlan(state),
  }),
  mapActionsToDispatchToProps({
    getRoles,
    addStylist,
  }),
)(CreateWaitlist);

export default CreateWaitlistHoc;
